import { canEditBillInUi, canEditPurchaseTransaction } from './billEditability';
import type { Bill, BillStatus } from '../types/bill';
import type { BillPayment } from '../types/billPayment';
import type { PurchaseTransaction } from '../types/purchaseTransaction';

/** ADMIN or ACCOUNTANT — create, edit, receive, and amend bills. */
export const canManageBills = (): boolean => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

/** ADMIN only — void bills and bill payments. */
export const canDeleteOrVoidPurchases = (): boolean =>
  localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

/** ADMIN or ACCOUNTANT — edit bill payment allocations. */
export const canEditBillPayment = (): boolean => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

export { canCostCorrectBill } from './billCostCorrection';

export const canEditBillTransaction = (
  bill: Pick<Bill, 'status' | 'balance' | 'total_amount' | 'received_into_inventory' | 'lines'>
): boolean => canManageBills() && canEditBillInUi(bill);

export const canEditBillFromRow = (txn: PurchaseTransaction): boolean =>
  txn.transaction_type === 'BILL' && canManageBills() && canEditPurchaseTransaction(txn);

export const canReceiveBill = (
  bill: Pick<Bill, 'status' | 'received_into_inventory'>
): boolean => canManageBills() && bill.status === 'DRAFT' && !bill.received_into_inventory;

export const canPayBill = (status: BillStatus): boolean =>
  status === 'OPEN' || status === 'PARTIAL';

export const canDeleteDraftBill = (bill: Pick<Bill, 'status'>): boolean =>
  canManageBills() && bill.status === 'DRAFT';

export const canVoidBill = (
  bill: Pick<Bill, 'status' | 'received_into_inventory'>
): boolean =>
  canDeleteOrVoidPurchases() && bill.status === 'OPEN' && bill.received_into_inventory;

export const canEditBillPaymentTransaction = (
  payment: Pick<BillPayment, 'status'>
): boolean => canEditBillPayment() && !isBillPaymentVoided(payment);

export const canEditBillPaymentFromRow = (txn: PurchaseTransaction): boolean =>
  txn.transaction_type === 'BILL_PAYMENT' &&
  canEditBillPayment() &&
  !isPaymentVoidedStatus(txn.status);

export const canVoidBillPaymentTransaction = (
  payment: Pick<BillPayment, 'status'>
): boolean => {
  const status = (payment.status ?? 'POSTED').toUpperCase();
  return canDeleteOrVoidPurchases() && status !== 'VOIDED';
};

export const canVoidBillPaymentFromRow = (txn: PurchaseTransaction): boolean => {
  if (txn.transaction_type !== 'BILL_PAYMENT' || !canDeleteOrVoidPurchases()) return false;
  if (isPaymentVoidedStatus(txn.status)) return false;
  return txn.available_actions.includes('void') || txn.status.toUpperCase() === 'POSTED';
};

export const isPaymentVoidedStatus = (status: string): boolean =>
  status.toUpperCase() === 'VOIDED';

export const isBillPaymentVoided = (payment: Pick<BillPayment, 'status'>): boolean =>
  (payment.status ?? 'POSTED').toUpperCase() === 'VOIDED';
